/**
 * StateManager - Handles localStorage persistence for player state
 * Enables state restoration across page navigation and browser sessions
 */
export class StateManager {
  constructor() {
    this.storageKey = 'hippsoc_podcast_player_state';
    this.autoSaveTimer = null;
  }

  /**
   * Save player state to localStorage
   * @param {Object} state - Player state object
   * @param {string} state.currentEpisodeUrl - Current episode URL
   * @param {number} state.currentTime - Current playback time in seconds
   * @param {boolean} state.isPlaying - Whether audio is playing
   * @param {number} state.playbackRate - Playback speed (1.0 = normal)
   * @param {number} state.volume - Volume level (0.0 to 1.0)
   * @param {number} state.currentIndex - Current episode index in playlist
   * @param {boolean} state.isDismissed - Whether player was dismissed/closed by user
   */
  save(state) {
    try {
      const stateToSave = {
        currentEpisodeUrl: state.currentEpisodeUrl,
        currentTime: state.currentTime || 0,
        isPlaying: state.isPlaying || false,
        playbackRate: state.playbackRate || 1.0,
        volume: state.volume || 1.0,
        currentIndex: state.currentIndex || 0,
        isDismissed: state.isDismissed || false,
        lastUpdated: Date.now()
      };

      localStorage.setItem(this.storageKey, JSON.stringify(stateToSave));
    } catch (error) {
      // localStorage might be disabled (private browsing)
      console.warn('Could not save player state:', error);
    }
  }

  /**
   * Load player state from localStorage
   * @returns {Object|null} Saved state or null if not found
   */
  load() {
    try {
      const savedState = localStorage.getItem(this.storageKey);
      if (!savedState) return null;

      const state = JSON.parse(savedState);

      // Don't restore if state is too old (> 7 days)
      const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
      if (state.lastUpdated && (Date.now() - state.lastUpdated > maxAge)) {
        this.clear();
        return null;
      }

      return state;
    } catch (error) {
      console.warn('Could not load player state:', error);
      return null;
    }
  }

  /**
   * Clear saved state
   */
  clear() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn('Could not clear player state:', error);
    }
  }

  /**
   * Auto-save state with debouncing
   * Saves state after a delay to avoid excessive writes
   * @param {Object} state - State to save
   * @param {number} delay - Debounce delay in milliseconds (default: 1000)
   */
  autoSave(state, delay = 1000) {
    // Clear existing timer
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }

    // Set new timer
    this.autoSaveTimer = setTimeout(() => {
      this.save(state);
      this.autoSaveTimer = null;
    }, delay);
  }

  /**
   * Save immediately and cancel any pending auto-save
   * @param {Object} state - State to save
   */
  saveImmediate(state) {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
    this.save(state);
  }

  /**
   * Check if localStorage is available
   * @returns {boolean}
   */
  isAvailable() {
    try {
      const testKey = '__test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }
}
